import { ShareAltOutlined } from "@ant-design/icons";
import { Dropdown, Menu, Skeleton, Space, Typography, message } from "antd";
import copy from "copy-to-clipboard";
import { PRODUCT_PROMO_TARGET_CUSTOMER_OPTIONS } from "features/Campaign/constants";
import { useGenerateLink } from "hooks/generateLink/query";
import { t } from "i18next";
import { formatCurrency, formatDate } from "utils/helperFuncs";
import { resolveProductPromoSellerSegmentType } from "features/Campaign/utils/productPromoTargetCustomer";

import "./index.scss";

const InfoOverView = ({ data, loading }) => {
  const { handleGenerateLink } = useGenerateLink();

  const getStatusConfig = (status) => {
    switch (status) {
      case "UPCOMING":
        return { className: "status-upcoming", label: t("campaign.upcoming"), color: "#ff7a45" };
      case "ENDED":
        return { className: "status-ended", label: t("campaign.ended"), color: "#ff4d4f" };
      case "ON_GOING":
        return { className: "status-ongoing", label: t("campaign.onGoing"), color: "#52c41a" };
      default:
        return { className: "", label: "", color: "" };
    }
  };

  const handleShareAppLink = async () => {
    let itemsCopy = data?.promotionCampaignTierGroups?.flatMap((item) =>
      item?.promotionCampaignTiers?.flatMap((group) => {
        return group.conditionProductGroups.flatMap((productGroup) =>
          productGroup.conditionPromotionProducts.map((promotionProduct) => promotionProduct.product.name)
        );
      })
    );

    try {
      const shopURL = process.env.REACT_APP_SHOP;
      const link = `${shopURL}/promotion_detail?promotion_id=${data?.id}`;
      await handleGenerateLink({ url: link }).then((res) => {
        const dynamicLayout = `${data?.name}
Xem tại: ${res?.data?.linkQuery?.generateDynamicLinkFromUrl}
${itemsCopy?.map((item) => `- ${item}`).join("\n")}`;

        copy(dynamicLayout);

        message.success(t("campaign.detailPromo.copyAppLinkSuccess"));
      });
    } catch (error) {
      message.error(t("campaign.detailPromo.copyLinkError"));
      console.info(error);
    }
  };

  const handleShareLandingLink = () => {
    copy(data?.shareLink);
    message.success(t("campaign.detailPromo.copyLandingLinkSuccess"));
  };

  const statusConfig = getStatusConfig(data?.status);

  const formatPeriod = () => {
    if (!data?.startDate && !data?.endDate) return "--";
    const start = data?.startDate ? formatDate(data.startDate) : "--";
    const end = data?.endDate ? formatDate(data.endDate) : "--";
    return `Từ ${start} đến ${end}`;
  };

  const formatChannels = () => {
    if (!data?.saleChannels || data.saleChannels.length === 0) return "";
    return data.saleChannels.map((channel) => channel?.name).join(", ");
  };

  const shareMenu = (
    <Menu>
      <Menu.Item key="app" onClick={handleShareAppLink}>
        {t("campaign.detailPromo.shareAppLinkMenu")}
      </Menu.Item>
      <Menu.Item key="landing" onClick={handleShareLandingLink}>
        {t("campaign.detailPromo.shareLandingLinkMenu")}
      </Menu.Item>
    </Menu>
  );

  const showShareMenu = ["ON_GOING", "UPCOMING"].includes(data?.status);

  return (
    <div className="info-overview">
      <Skeleton active loading={loading} paragraph={{ rows: 2 }}>
        <div className="info-overview__title-row">
          <Typography.Text className="campaign-title">{data?.name || "--"}</Typography.Text>
          {showShareMenu && (
            <Dropdown overlay={shareMenu} trigger={["click"]} placement="bottomLeft">
              <span className="share-icon-wrapper">
                <ShareAltOutlined className="share-icon" />
              </span>
            </Dropdown>
          )}
        </div>

        <div className="info-overview__info-row">
          <div className="info-item">
            <Space size={8} align="center">
              <span className={`status-dot ${statusConfig.className}`} />
              <Typography.Text style={{ color: statusConfig.color, fontWeight: 500 }}>
                {statusConfig.label}
              </Typography.Text>
            </Space>
          </div>

          <div className="info-item">
            <Typography.Text className="detail-label">{t("campaign.detailPromo.periodLabel")}</Typography.Text>
            <Typography.Text className="detail-value">{formatPeriod()}</Typography.Text>
          </div>

          {data?.saleChannels && data.saleChannels.length > 0 && (
            <div className="info-item">
              <Typography.Text className="detail-label">{t("campaign.detailPromo.saleChannelLabel")}</Typography.Text>
              <Typography.Text className="detail-value">{formatChannels()}</Typography.Text>
            </div>
          )}

          {(() => {
            const sellerUserSegmentType = resolveProductPromoSellerSegmentType(data?.sellerSegment?.type);
            const label = PRODUCT_PROMO_TARGET_CUSTOMER_OPTIONS.find(
              (opt) => opt.value === sellerUserSegmentType
            )?.label;
            return (
              <div className="info-item">
                <Typography.Text className="detail-label">
                  {t("campaign.detailPromo.targetCustomerLabel")}
                </Typography.Text>
                <Typography.Text className="detail-value">{label || "--"}</Typography.Text>
              </div>
            );
          })()}

          {data?.maxBudgetPerSeller > 0 && (
            <div className="info-item">
              <Typography.Text className="detail-label">{t("campaign.detailPromo.programBudgetLabel")}</Typography.Text>
              <Typography.Text className="detail-value">{formatCurrency(data.maxBudgetPerSeller)}</Typography.Text>
            </div>
          )}
        </div>
      </Skeleton>
    </div>
  );
};

export default InfoOverView;
